/**
 * O bloco "Convênio" no cadastro do cliente (#811, clínica).
 *
 * ── Só com a clínica ligada ──────────────────────────────────────────────
 *
 * Como a ficha do paciente: quem decide se o bloco existe é o cadastro, e ele
 * só é montado com a funcionalidade "clinica" ligada.
 *
 * ── Um paciente, vários convênios ────────────────────────────────────────
 *
 * O principal vem primeiro, com selo e a borda da marca (16/10/2026); os
 * outros são uma linha cada, com "Tornar principal". Cada convênio se edita ou
 * se remove no lugar, e o formulário de acrescentar é o mesmo do editar — dois formulários
 * para os mesmos campos são duas chances de um ficar para trás.
 *
 * ── A carteirinha ────────────────────────────────────────────────────────
 *
 * Só os quatro últimos caracteres aparecem: o número inteiro nunca volta da
 * API. Por isso, na edição, o campo nasce vazio e vazio é "manter".
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErroDaApi, type ConvenioDoPaciente } from '@cliqcrm/contracts';
import { useApi, useConveniosDoPaciente, useOperadoras } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, CampoSelecao, Chip, Ficha, Selo, type LinhaFicha } from '@/shared/ui';
import { Carregando, Erro } from '@/shared/ui/estados/estados';
import {
  corpoParaCriar,
  corpoParaEditar,
  formularioDe,
  formularioNovo,
  mesDaValidade,
  operadorasParaEscolher,
  tituloDoConvenio,
  vencida,
  type FormularioDoConvenio,
} from '../lib/convenio';
import { hojeLocal } from '../lib/ficha-do-paciente';
import { Bloco } from './bloco';
import './bloco-do-convenio.css';

export function ConveniosNoCliente({
  clienteId,
  podeEditar,
}: {
  clienteId: string;
  podeEditar: boolean;
}): ReactNode {
  const consulta = useConveniosDoPaciente(clienteId);
  const [editando, definirEditando] = useState<string | 'novo' | null>(null);

  if (consulta.isPending) {
    return (
      <Bloco titulo="Convênio">
        <Carregando forma="ficha" linhas={2} rotulo="Carregando os convênios" />
      </Bloco>
    );
  }
  if (consulta.isError) {
    return (
      <Bloco titulo="Convênio">
        <Erro
          erro={consulta.error}
          titulo="Não foi possível abrir os convênios"
          aoRepetir={() => void consulta.refetch()}
        />
      </Bloco>
    );
  }

  const convenios = consulta.data.itens;
  const fechar = () => definirEditando(null);

  return (
    <Bloco
      titulo="Convênio"
      largo={editando !== null}
      acao={
        podeEditar &&
        editando === null && (
          <Botao miudo variante="fantasma" onClick={() => definirEditando('novo')}>
            Acrescentar convênio
          </Botao>
        )
      }
    >
      <div className="convenio-cli">
        {convenios.length === 0 && editando !== 'novo' && (
          <p className="convenio-cli__nota">Nenhum convênio cadastrado.</p>
        )}

        {convenios.length > 0 && (
          <ul className="convenio-cli__lista" aria-label="Convênios do paciente">
            {convenios.map((convenio) =>
              editando === convenio.id ? (
                <li key={convenio.id}>
                  <ConvenioEmEdicao clienteId={clienteId} convenio={convenio} aoFechar={fechar} />
                </li>
              ) : (
                <LinhaDoConvenio
                  key={convenio.id}
                  clienteId={clienteId}
                  convenio={convenio}
                  podeEditar={podeEditar && editando === null}
                  aoEditar={() => definirEditando(convenio.id)}
                />
              ),
            )}
          </ul>
        )}

        {editando === 'novo' && (
          <ConvenioEmEdicao
            clienteId={clienteId}
            semNenhum={convenios.length === 0}
            aoFechar={fechar}
          />
        )}
      </div>
    </Bloco>
  );
}

function LinhaDoConvenio({
  clienteId,
  convenio,
  podeEditar,
  aoEditar,
}: {
  clienteId: string;
  convenio: ConvenioDoPaciente;
  podeEditar: boolean;
  aoEditar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [confirmando, definirConfirmando] = useState(false);
  const titulo = tituloDoConvenio(convenio);

  const invalidar = () =>
    void cache.invalidateQueries({ queryKey: chaves.conveniosDoPaciente(clienteId) });

  const remover = useMutation({
    mutationFn: () =>
      api!.removerConvenioDoPaciente({ caminho: { id: clienteId, convenioId: convenio.id } }),
    onSuccess: invalidar,
  });
  // Tornar principal é editar com `principal: true`: a carteirinha vazia é "manter".
  const tornarPrincipal = useMutation({
    mutationFn: () =>
      api!.editarConvenioDoPaciente({
        caminho: { id: clienteId, convenioId: convenio.id },
        corpo: corpoParaEditar({ ...formularioDe(convenio), principal: true }, convenio.versao),
      }),
    onSuccess: invalidar,
  });

  const vencido = vencida(convenio.validade, hojeLocal());
  const linhas: LinhaFicha[] =
    convenio.modalidade === 'particular'
      ? []
      : [
          {
            rotulo: 'Plano',
            valor: convenio.plano ?? 'não informado',
            vazio: convenio.plano === null,
          },
          {
            rotulo: 'Carteirinha',
            valor: convenio.carteirinha_final
              ? `•••• ${convenio.carteirinha_final}`
              : 'não informada',
            vazio: convenio.carteirinha_final === null,
          },
          {
            rotulo: 'Validade',
            valor: convenio.validade ? mesDaValidade(convenio.validade) : 'não informada',
            vazio: convenio.validade === null,
          },
        ];

  return (
    <li
      className={
        convenio.principal
          ? 'convenio-cli__item convenio-cli__item--principal'
          : 'convenio-cli__item'
      }
    >
      <div className="convenio-cli__cabeca">
        <span className="convenio-cli__titulo">{titulo}</span>
        {convenio.principal && <Selo tom="ok">Principal</Selo>}
        {!convenio.titular && <Chip>Dependente</Chip>}
        {vencido && <Chip tom="atencao">Vencida</Chip>}
        {podeEditar && !confirmando && (
          <span className="convenio-cli__comandos">
            {!convenio.principal && (
              <Botao
                miudo
                variante="fantasma"
                aria-label={`Tornar ${titulo} o principal`}
                desabilitado={tornarPrincipal.isPending}
                motivo="Salvando…"
                onClick={() => tornarPrincipal.mutate()}
              >
                Tornar principal
              </Botao>
            )}
            <Botao miudo variante="fantasma" aria-label={`Editar ${titulo}`} onClick={aoEditar}>
              Editar
            </Botao>
            <Botao
              miudo
              variante="fantasma"
              aria-label={`Remover ${titulo}`}
              onClick={() => definirConfirmando(true)}
            >
              Remover
            </Botao>
          </span>
        )}
      </div>
      {convenio.modalidade === 'particular' ? (
        <span className="convenio-cli__detalhe">Sem operadora, plano nem carteirinha.</span>
      ) : (
        <Ficha larguraRotulo="110px" linhas={linhas} />
      )}

      {podeEditar && confirmando && (
        <div className="convenio-cli__confirmar">
          <span className="convenio-cli__detalhe">Remover este convênio?</span>
          <Botao
            miudo
            variante="perigo"
            desabilitado={remover.isPending}
            motivo="Removendo…"
            onClick={() => remover.mutate()}
          >
            Remover
          </Botao>
          <Botao miudo variante="fantasma" onClick={() => definirConfirmando(false)}>
            Cancelar
          </Botao>
        </div>
      )}
      {remover.isError && (
        <p className="convenio-cli__erro" role="alert">
          {recadoDeErro(remover.error, { generica: 'Não foi possível remover o convênio.' })}
        </p>
      )}
      {tornarPrincipal.isError && (
        <p className="convenio-cli__erro" role="alert">
          {tornarPrincipal.error instanceof ErroDaApi &&
          tornarPrincipal.error.codigo === 'CONFLITO_DE_VERSAO'
            ? 'Outra pessoa mudou este convênio. Recarregue a página e tente de novo.'
            : recadoDeErro(tornarPrincipal.error, {
                generica: 'Não foi possível trocar o principal.',
              })}
        </p>
      )}
    </li>
  );
}

function ConvenioEmEdicao({
  clienteId,
  convenio,
  semNenhum = false,
  aoFechar,
}: {
  clienteId: string;
  convenio?: ConvenioDoPaciente;
  semNenhum?: boolean;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const operadoras = useOperadoras(true);
  const [formulario, definirFormulario] = useState<FormularioDoConvenio>(() =>
    convenio ? formularioDe(convenio) : formularioNovo(semNenhum),
  );
  const mudar = <C extends keyof FormularioDoConvenio>(campo: C, valor: FormularioDoConvenio[C]) =>
    definirFormulario((atual) => ({ ...atual, [campo]: valor }));

  const salvar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      if (convenio) {
        return api.editarConvenioDoPaciente({
          caminho: { id: clienteId, convenioId: convenio.id },
          corpo: corpoParaEditar(formulario, convenio.versao),
        });
      }
      return api.criarConvenioDoPaciente({
        caminho: { id: clienteId },
        corpo: corpoParaCriar(formulario),
        // Chave por tentativa: o segundo clique não cria o convênio duas vezes.
        idempotencyKey:
          globalThis.crypto?.randomUUID?.() ?? `convenio-${Date.now()}-${Math.random()}`,
      });
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.conveniosDoPaciente(clienteId) });
      aoFechar();
    },
  });

  const conflito =
    salvar.error instanceof ErroDaApi && salvar.error.codigo === 'CONFLITO_DE_VERSAO';
  const particular = formulario.modalidade === 'particular';
  const opcoes = operadorasParaEscolher(operadoras.data?.itens ?? [], convenio?.operadora ?? null);
  const prefixo = convenio ? `convenio-${convenio.id}` : 'convenio-novo';

  return (
    <form
      className="convenio-cli__formulario"
      onSubmit={(evento) => {
        evento.preventDefault();
        salvar.mutate();
      }}
    >
      <fieldset className="convenio-cli__grupo">
        <legend>{convenio ? 'Editar convênio' : 'Novo convênio'}</legend>
        <CampoSelecao
          id={`${prefixo}-modalidade`}
          rotulo="Modalidade"
          value={formulario.modalidade}
          onChange={(e) =>
            mudar('modalidade', e.currentTarget.value === 'particular' ? 'particular' : 'convenio')
          }
        >
          <option value="convenio">Convênio</option>
          <option value="particular">Particular</option>
        </CampoSelecao>

        {!particular && (
          <>
            <CampoSelecao
              id={`${prefixo}-operadora`}
              rotulo="Operadora"
              value={formulario.operadoraId}
              required
              onChange={(e) => mudar('operadoraId', e.currentTarget.value)}
            >
              <option value="">Escolha a operadora</option>
              {opcoes.map((opcao) => (
                <option key={opcao.id} value={opcao.id}>
                  {opcao.nome}
                </option>
              ))}
            </CampoSelecao>
            {operadoras.isSuccess && opcoes.length === 0 && (
              <p className="convenio-cli__nota" role="status">
                Nenhuma operadora cadastrada. Quem administra a organização cadastra em
                Configurações, em "Operadoras de convênio".
              </p>
            )}
            <Campo
              id={`${prefixo}-plano`}
              rotulo="Plano"
              type="text"
              value={formulario.plano}
              maxLength={120}
              onChange={(e) => mudar('plano', e.currentTarget.value)}
            />
            <Campo
              id={`${prefixo}-carteirinha`}
              rotulo="Carteirinha"
              type="text"
              value={formulario.carteirinha}
              maxLength={40}
              disabled={formulario.tirarCarteirinha}
              placeholder={
                convenio?.carteirinha_final ? `•••• ${convenio.carteirinha_final}` : undefined
              }
              ajuda={
                formulario.tirarCarteirinha
                  ? 'Será tirada ao salvar.'
                  : convenio?.carteirinha_final
                    ? 'Guardada cifrada: não dá para mostrar inteira. Digite para trocar.'
                    : 'Guardada cifrada. Só os quatro últimos caracteres aparecem depois.'
              }
              onChange={(e) => mudar('carteirinha', e.currentTarget.value)}
            />
            {convenio?.carteirinha_final && (
              <div>
                <Botao
                  variante="fantasma"
                  miudo
                  type="button"
                  onClick={() => {
                    mudar('tirarCarteirinha', !formulario.tirarCarteirinha);
                    mudar('carteirinha', '');
                  }}
                >
                  {formulario.tirarCarteirinha ? 'Manter a carteirinha' : 'Tirar a carteirinha'}
                </Botao>
              </div>
            )}
            <Campo
              id={`${prefixo}-validade`}
              rotulo="Validade"
              type="date"
              value={formulario.validade}
              onChange={(e) => mudar('validade', e.currentTarget.value)}
            />
          </>
        )}

        <label className="convenio-cli__marca">
          <input
            type="checkbox"
            checked={formulario.titular}
            onChange={(e) => mudar('titular', e.currentTarget.checked)}
          />
          O paciente é o titular do plano
        </label>
        <label className="convenio-cli__marca">
          <input
            type="checkbox"
            checked={formulario.principal}
            // O principal não se desmarca: para trocar, marca-se outro.
            disabled={convenio?.principal === true || semNenhum}
            onChange={(e) => mudar('principal', e.currentTarget.checked)}
          />
          Convênio principal
        </label>
      </fieldset>

      {salvar.isError && (
        <p className="convenio-cli__erro" role="alert">
          {conflito
            ? 'Outra pessoa mudou este convênio enquanto você editava. Recarregue para ver a versão atual e refaça a sua alteração.'
            : recadoDeErro(salvar.error, { generica: 'Não foi possível salvar o convênio.' })}
        </p>
      )}

      <div className="convenio-cli__acoes">
        {conflito ? (
          <Botao
            variante="primario"
            type="button"
            onClick={() => {
              void cache.invalidateQueries({ queryKey: chaves.conveniosDoPaciente(clienteId) });
              aoFechar();
            }}
          >
            Recarregar os convênios
          </Botao>
        ) : (
          <Botao
            variante="primario"
            type="submit"
            desabilitado={salvar.isPending}
            motivo="Salvando…"
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Botao>
        )}
        <Botao variante="fantasma" type="button" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}
